import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faSave,
  faArrowLeft,
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { userService } from '../../../services/userService';

function UserProfile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => {
    const localUser = localStorage.getItem('user');
    return localUser
      ? JSON.parse(localUser)
      : { name: '', email: '', phone: '', address: '', state: 'Lagos' };
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await userService.getMe();
        const me = data?.user;
        if (me) {
          const merged = {
            customId: me.customId || '',
            referralCode: me.referralCode || user.referralCode || '',
            name: me.name || user.name || '',
            email: me.email || user.email || '',
            phone: me.phone || user.phone || '',
            address: me.address || user.address || '',
            state: me.state || user.state || 'Lagos',
          };
          localStorage.setItem('user', JSON.stringify(merged));
          setUser(merged);
        }
      } catch (_) {
        // ignore and keep local state
      }
    })();
    return () => { cancelled = true };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateProfile = () => {
    const name = (user.name || '').trim();
    const email = (user.email || '').trim();
    const phone = (user.phone || '').trim();
    const address = (user.address || '').trim();
    const state = (user.state || '').trim();

    if (!name || name.length < 2) return 'Please enter your full name.';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address.';

    const phoneDigits = phone.replace(/\D/g, '');
    if (!phoneDigits || phoneDigits.length < 7) return 'Please enter a valid phone number.';

    if (!state) return 'Please select your state.';

    if (!address || address.length < 5) return 'Please enter a valid address.';

    return null;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const validationError = validateProfile();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const updated = await userService.updateMe({ name: user.name.trim(), address: user.address.trim(), phone: (user.phone || '').trim(), state: (user.state || '').trim() });
      const mergedUser = { ...updated, email: user.email };
      localStorage.setItem('user', JSON.stringify(mergedUser));
      setUser(mergedUser);
      setSuccess(true);
      navigate('/dashboard/user', { state: { profileUpdated: true } });
    } catch (err) {
      console.error('Profile update failed', err);
      setError(err?.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  const handleUpgradeToEcommerce = async () => {
    setError('');
    const validationError = validateProfile();
    if (validationError) {
      setError(`Please fix your profile first: ${validationError}`);
      return;
    }

    setLoading(true);
    try {
      // First, save any pending changes to ensure data is consistent
      const updated = await userService.updateMe({ name: user.name.trim(), address: user.address.trim(), phone: (user.phone || '').trim(), state: (user.state || '').trim() });
      const mergedUser = { ...updated, email: user.email };
      localStorage.setItem('user', JSON.stringify(mergedUser));
      setUser(mergedUser);
      
      // Now, proceed to plan selection
      navigate('/auth/plan-selection');
    } catch (e) {
      setError(e?.message || 'Failed to save profile before upgrading. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Nigerian states for dropdown
  const nigerianStates = [
    'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno', 
    'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT (Abuja)', 'Gombe', 
    'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 
    'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto', 
    'Taraba', 'Yobe', 'Zamfara'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex items-center">
            <button 
              onClick={() => navigate('/dashboard/user')}
              className="mr-4 w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 animate-fade-in">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center shadow-sm">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-3 text-red-500" />
              <span>{error}</span>
            </div>
          </div>
        )}
        
        {success && (
          <div className="mb-6 animate-fade-in">
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center shadow-sm">
              <FontAwesomeIcon icon={faCheckCircle} className="mr-3 text-green-500" />
              <span>Profile updated successfully!</span>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-br from-red-500 to-red-600 p-6 text-white">
                <div className="flex flex-col items-center">
                  <div className="w-24 h-24 rounded-full bg-white bg-opacity-20 backdrop-blur-sm flex items-center justify-center mb-4">
                    <FontAwesomeIcon icon={faUser} className="text-white text-4xl" />
                  </div>
                  <h2 className="text-xl font-bold">{user.name || 'Your Name'}</h2>
                  <p className="text-red-100 mt-1">{user.email || 'your.email@example.com'}</p>
                  {user.customId && (
                    <div className="mt-2 px-3 py-1 bg-white bg-opacity-20 backdrop-blur-md rounded-full border border-white border-opacity-30">
                      <span className="text-xs font-mono tracking-wider text-white">ID: {user.customId}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mr-3">
                      <FontAwesomeIcon icon={faEnvelope} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="text-sm font-medium text-gray-800">{user.email || 'Not provided'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center mr-3">
                      <FontAwesomeIcon icon={faPhone} className="text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="text-sm font-medium text-gray-800">{user.phone || 'Not provided'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center mr-3">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Location</p>
                      <p className="text-sm font-medium text-gray-800">{user.state || 'Not provided'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Info Card */}
            <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200 mt-6">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-800">Profile Information</p>
                  <p className="text-xs text-blue-700 mt-1">Your profile information helps us provide you with a personalized experience. Only your name and address are stored on our servers.</p>
                </div>
              </div>
            </div>

          </div>
          
          {/* Form Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Personal Information</h2>
              
              <form onSubmit={handleProfileSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <div className="relative">
                      <input
                        type="text"
                        name="name"
                        value={user.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                        placeholder="Enter your full name"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <FontAwesomeIcon icon={faUser} className="text-gray-400" />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                    <div className="relative">
                      <input
                        type="email"
                        name="email"
                        value={user.email}
                        onChange={handleInputChange}
                        readOnly
                        disabled
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                        placeholder="Enter your email address"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                    <div className="relative">
                      <input
                        type="tel"
                        name="phone"
                        value={user.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                        placeholder="Enter your phone number"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <FontAwesomeIcon icon={faPhone} className="text-gray-400" />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                    <div className="relative">
                      <select
                        name="state"
                        value={user.state}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors appearance-none"
                      >
                        {nigerianStates.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                    <div className="relative">
                      <textarea
                        name="address"
                        value={user.address}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                        placeholder="Enter your full address"
                        rows="3"
                      ></textarea>
                      <div className="absolute top-3 right-3 pointer-events-none">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 flex items-center transition-colors transform transition-transform duration-300 hover:scale-105 active:scale-95"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Upgrade to Ecommerce */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 mt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Access ecommerce booking, custom pricing, and shipment quotas.</p>
                  <p className="text-lg font-semibold text-gray-800">Upgrade to Ecommerce</p>
                </div>
                <button
                  onClick={handleUpgradeToEcommerce}
                  disabled={loading}
                  className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50"
                >
                  Upgrade
                </button>
              </div>
            </div>
            
            {/* Security Notice */}
            <div className="bg-yellow-50 rounded-2xl p-6 border border-yellow-200 mt-6">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center mr-3 flex-shrink-0">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-yellow-800">Security Information</p>
                  <p className="text-xs text-yellow-700 mt-1">We take your privacy seriously. Your personal information is encrypted and securely stored. We never share your data with third parties without your consent.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserProfile;
